// src/components/layout/NotificationItem.tsx
// ============================================================
// SALT — Single Notification Row
//
// Renders inside NotificationPanel. Shows:
//   - Category icon (team/match/dispute/rank/season/admin)
//   - Title + message
//   - Relative timestamp
//   - Unread indicator (left accent border)
//
// Clicking marks as read and navigates to actionUrl if present.
// ============================================================

import React from 'react';
import type { Notification, NotificationCategory } from '../../types/notification.types';
import { getNotificationCategory } from '../../types/notification.types';
import { formatRelativeTime } from '../../utils/formatters';

// ── Props ───────────────────────────────────────────────────

interface NotificationItemProps {
  notification: Notification;
  onMarkRead: (notificationId: number) => void;
  onNavigate: (url: string) => void;
}

// ── Category Icons (inline SVGs) ────────────────────────────

function CategoryIcon({ category }: { category: NotificationCategory }) {
  const size = 16;
  const commonProps = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  switch (category) {
    case 'team':
      // Users/people icon
      return (
        <svg {...commonProps}>
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87" />
          <path d="M16 3.13a4 4 0 010 7.75" />
        </svg>
      );

    case 'match':
      // Crosshairs/target icon (competitive)
      return (
        <svg {...commonProps}>
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="6" />
          <circle cx="12" cy="12" r="2" />
          <line x1="12" y1="2" x2="12" y2="6" />
          <line x1="12" y1="18" x2="12" y2="22" />
          <line x1="2" y1="12" x2="6" y2="12" />
          <line x1="18" y1="12" x2="22" y2="12" />
        </svg>
      );

    case 'dispute':
      // Alert triangle
      return (
        <svg {...commonProps}>
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );

    case 'rank':
      // Trophy icon
      return (
        <svg {...commonProps}>
          <path d="M6 9H4.5a2.5 2.5 0 010-5H6" />
          <path d="M18 9h1.5a2.5 2.5 0 000-5H18" />
          <path d="M4 22h16" />
          <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
          <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
          <path d="M18 2H6v7a6 6 0 0012 0V2z" />
        </svg>
      );

    case 'season':
      // Clock icon
      return (
        <svg {...commonProps}>
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      );

    case 'admin':
    default:
      // Megaphone/announcement icon
      return (
        <svg {...commonProps}>
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
      );
  }
}

// ── Component ───────────────────────────────────────────────

export default function NotificationItem({
  notification,
  onMarkRead,
  onNavigate,
}: NotificationItemProps) {
  const { notificationId, type, title, message, actionUrl, isRead, createdAt } = notification;

  const category = getNotificationCategory(type);
  const timeAgo = formatRelativeTime(createdAt);

  const handleClick = () => {
    if (!isRead) {
      onMarkRead(notificationId);
    }
    if (actionUrl) {
      onNavigate(actionUrl);
    }
  };

  return (
    <button
      type="button"
      className={`sl-notif-item ${!isRead ? 'sl-notif-item--unread' : ''} ${actionUrl ? 'sl-notif-item--clickable' : ''}`}
      onClick={handleClick}
    >
      <div className={`sl-notif-icon sl-notif-icon--${category}`}>
        <CategoryIcon category={category} />
      </div>

      <div className="sl-notif-content">
        <div className="sl-notif-title">{title}</div>
        <div className="sl-notif-message">{message}</div>
      </div>

      <div className="sl-notif-meta">
        <span className="sl-notif-time">{timeAgo}</span>
        {!isRead && <span className="sl-notif-dot" aria-label="Unread" />}
      </div>
    </button>
  );
}